// Where the calls come from and where reviews go — the same for every section (user 7 Oct 2026).
//   My data   = analysisItems of the user's own stations (private + public broadcasts)
//   Community = analysisItems with public == true (broadcasts shared with the community), all stations
// One analysisItem = one call (signal) or one 10 s piece; it points to the engine's analysis bundle on S3
// (bundleKey, + callId when the bundle holds several calls). Only items with status "open" are listed; a reviewed
// item is "done" for everyone and never loaded again (DATA_MODEL.md §3-4).
// Saving (rules in firestore.rules, analysisItems): claim (open -> in_progress by me, in a transaction) → one batch:
//   edits/{auto id}  = the full review document (reviewModel.toReviewDoc: lines, boxes, marks, parts, removed engine
//                      items, label, species, measurements, frame) — versions, never overwritten
//   label/current    = final code / broad class / species
//   verdicts/{uid}   = confirmed | dismissed (+ reason)
//   item             → done (doneBy, doneAt) — or, when the species was corrected, back to "open" in the new section
import { collection, deleteDoc, doc, getDoc, getDocs, limit, orderBy, query, runTransaction, serverTimestamp, setDoc, updateDoc, where, writeBatch } from 'firebase/firestore';
import { makeAssets, loadStarter, starterAssets, starterUrl } from './libraryModel';
import { httpsCallable } from 'firebase/functions';
import { toFirestore, fromFirestore } from './fsSafe';

export const SCOPES = [['mine', 'My data'], ['community', 'Community']];
export const SCOPE_NOTE = {
  mine: 'Your own stations’ broadcasts (private and public). Only you and your station’s analysts see the private ones.',
  community: 'Public broadcasts from all stations, shared with the community.',
  all: 'Admin: every station’s broadcasts, private ones included.',
};
const STALE_MS = 2 * 3600 * 1000;
const PAGE = 20;
const META = 600;            // open items read per list (small documents): many recordings
const PER_RECORDING = 80;    // bundles loaded for the chosen recording
// one recording = station + file; its date = recordedAt (else when the engine wrote it)
const toMs = (v) => (v?.toMillis ? v.toMillis() : (v ? Date.parse(v) || 0 : 0));
export const recKey = (it) => `${it.stationId || ''}|${it.fileId || it.file || ''}`;
export function recordingsOf(items) {
  const m = new Map();
  items.forEach((it) => {
    const k = recKey(it);
    const r = m.get(k) || { key: k, stationId: it.stationId || '', fileId: it.fileId || '', file: it.file || '', recordedMs: toMs(it.recordedAt), addedMs: 0, n: 0 };
    r.n += 1; r.addedMs = Math.max(r.addedMs, toMs(it.createdAt)); if (!r.recordedMs) r.recordedMs = toMs(it.recordedAt);
    m.set(k, r);
  });
  return [...m.values()].sort((a, b) => (b.recordedMs || b.addedMs) - (a.recordedMs || a.addedMs));
}

export function myStationIds(userData) {
  const ids = [...(userData?.stationIds || [])];
  if (userData?.stationId && !ids.includes(String(userData.stationId))) ids.push(String(userData.stationId));
  return ids.map(String).filter(Boolean);
}

const dirOf = (key) => key.split('/').slice(0, -1).join('/');

/** noiseLevels for one or more stations (or the public ones) + the stations' names / latitude */
export async function noiseList(db, { stationIds = null, publicOnly = false } = {}) {
  const base = collection(db, 'noiseLevels');
  const w = [orderBy('recordedAt', 'desc'), limit(3000)];
  const qs = stationIds ? stationIds.slice(0, 10).map((sid) => query(base, where('stationId', '==', String(sid)), ...w))
    : [publicOnly ? query(base, where('public', '==', true), ...w) : query(base, ...w)];
  const seen = new Set();
  const docs = (await Promise.all(qs.map((q) => getDocs(q)))).flatMap((sn) => sn.docs.map((d) => ({ id: d.id, ...d.data() })))
    .filter((x) => !seen.has(x.id) && seen.add(x.id));
  const stations = {};
  await Promise.all([...new Set(docs.map((d) => d.stationId))].map(async (sid) => {
    try { const sd = (await getDoc(doc(db, 'stations', String(sid)))).data() || {}; stations[sid] = { name: sd.name || sd.stationName || `Station ${sid}`, lat: sd.lat ?? sd.latitude ?? null }; } catch (e) { stations[sid] = { name: `Station ${sid}` }; }
  }));
  return { docs, stations };
}

/** the station dashboard's noise tab: only this station */
export const stationNoiseSource = (db, stationId) => ({ list: () => noiseList(db, { stationIds: [String(stationId)] }) });

/** preview: real engine values for two example recordings + a SYNTHETIC year (public/analysis-demo/noise.json) */
export const demoNoise = {
  data: null,
  async list(scope) {
    if (!this.data) {
      const r = await fetch(`${process.env.PUBLIC_URL || ''}/analysis-demo/noise.json`);
      if (!r.ok) throw new Error(`preview noise data ${r.status}`);
      this.data = await r.json();
    }
    const docs = scope === 'mine' ? this.data.docs.filter((d) => ['502', 'example-synthetic'].includes(d.stationId)) : this.data.docs;
    return { docs, stations: this.data.stations };
  },
};

export function firestoreSource({ db, functions, user, userData }) {
  const stations = myStationIds(userData);
  // read links for an item's bundle and the files next to it, checked against the item's visibility (8 Oct 2026:
  // analysisFiles.ts — the old getS3PresignedUrl signed any key for anyone)
  const fileUrlFn = httpsCallable(functions, 'analysisFileUrl');
  const bundles = new Map();
  const itemUrl = async (item, key) => (await fileUrlFn({ itemId: item.id, key })).data.url;

  async function bundleFor(item) {
    const url = (_bucket, key) => itemUrl(item, key);
    if (!bundles.has(item.bundleKey)) {
      bundles.set(item.bundleKey, (async () => {
        const r = await fetch(await url(null, item.bundleKey));
        if (!r.ok) throw new Error(`analysis bundle ${r.status}`);
        return r.json();
      })());
    }
    const b = await bundles.get(item.bundleKey);
    // 13 Oct 2026: an item whose call is not in the (newer) bundle any more is not shown — before, it fell back to
    // the bundle's first call, so stale items of an older engine run all showed "Manatee call 1"
    const entry = item.callId ? (b.calls || []).find((x) => x.id === item.callId) : (b.calls || [])[0];
    if (!entry) return null;
    // every picture of the bundle (spectrogram, engine figures) is a key next to the bundle → signed URL
    // relative paths sit next to the bundle; "key:<s3 key>" = a file elsewhere under the file's engine results
    // (e.g. the recording-level figures shared by all pieces of a file)
    const sign = (p) => (p && p.startsWith('key:') ? url(null, p.slice(4))
      : (p && !/^https?:|^\//.test(p) ? url(null, `${dirOf(item.bundleKey)}/${p}`) : Promise.resolve(p)));
    const signMap = async (m) => Object.fromEntries(await Promise.all(Object.entries(m || {}).map(async ([k, v]) => [k, await sign(v)])));
    const img = await sign(entry.img);
    if (entry.audio && entry.audio.url) entry.audio = { ...entry.audio, url: await sign(entry.audio.url) };
    // 0.4.12: the longer sound clip around the call (live spectrogram + playback)
    if (entry.ctx && entry.ctx.url) entry.ctx = { ...entry.ctx, url: await sign(entry.ctx.url) };
    if (entry.figures) entry.figures = await signMap(entry.figures);
    if (entry.fileFigures) entry.fileFigures = await signMap(entry.fileFigures);
    // full-resolution signals of a pulsed piece (int16, next to the bundle): loaded only when the reviewer adds pulses
    if (entry.signals) entry.signalUrls = await signMap({ [entry.signals.bandpassed]: entry.signals.bandpassed, [entry.signals.raw]: entry.signals.raw });
    if (entry.groupFigures) entry.groupFigures = Object.fromEntries(await Promise.all(Object.entries(entry.groupFigures).map(async ([g, m]) => [g, await signMap(m)])));
    return { ...entry, id: item.id, img, speciesId: item.speciesId ?? entry.speciesId ?? null,
      species: item.speciesLabel || entry.species || null, speciesSource: item.speciesSource || null, _item: item,
      _meta: { itemId: item.id, stationId: item.stationId, fileId: item.fileId, bundleKey: item.bundleKey, engineVersion: item.engineVersion || null } };
  }

  // To approve: decoders' reviews (status to_approve) with their latest review loaded into the editor
  async function listApprove(category) {
    const base = collection(db, 'analysisItems');
    const w = [where('category', '==', category), where('status', '==', 'to_approve'), orderBy('createdAt', 'desc'), limit(PAGE)];
    // admins: all items; researchers: community items + their own stations' items (what the security rules allow)
    const qs = userData?.role === 'admin' ? [query(base, ...w)]
      : [query(base, where('public', '==', true), ...w), ...stations.slice(0, 10).map((sid) => query(base, where('stationId', '==', sid), ...w))];
    const seen = new Set();
    const items = (await Promise.all(qs.map((q) => getDocs(q)))).flatMap((sn) => sn.docs.map((d) => ({ id: d.id, ...d.data() })))
      .filter((x) => x.bundleKey && !seen.has(x.id) && seen.add(x.id));
    const calls = await Promise.all(items.map(async (it) => {
      const e = await bundleFor(it).catch(() => null);
      if (!e) return null;
      const last = await getDocs(query(collection(db, 'analysisItems', it.id, 'edits'), orderBy('createdAt', 'desc'), limit(1)));
      const prev = last.docs[0];
      return prev ? { ...e, _prev: fromFirestore(prev.data()), _prevId: prev.id } : e;
    }));
    return calls.filter(Boolean);
  }

  // final results (analysisResults, written by the Cloud Function when a review becomes final)
  async function results(category, scope) {
    const base = collection(db, 'analysisResults');
    const w = [where('category', '==', category), orderBy('finalAt', 'desc'), limit(500)];
    const qs = scope === 'mine' ? stations.slice(0, 10).map((sid) => query(base, where('stationId', '==', sid), ...w))
      : (scope === 'all' && userData?.role === 'admin' ? [query(base, ...w)] : [query(base, where('public', '==', true), ...w)]);
    const seen = new Set();
    return (await Promise.all(qs.map((q) => getDocs(q)))).flatMap((sn) => sn.docs.map((d) => ({ id: d.id, ...d.data() })))
      .filter((x) => !seen.has(x.id) && seen.add(x.id))
      .map((x) => ({ ...x, finalAt: x.finalAt?.toDate ? x.finalAt.toDate().toISOString() : x.finalAt, recordedAt: x.recordedAt?.toDate ? x.recordedAt.toDate().toISOString() : x.recordedAt }));
  }

  // call library (library/{id}): researchers / admins add, an admin approves; files on S3 under library/{id}/
  const uploadUrls = httpsCallable(functions, 'libraryUploadUrls');
  const libFile = httpsCallable(functions, 'libraryFileUrl');      // GET link, checked against the entry's visibility
  const libRows = (sn) => sn.docs.map((d) => ({ id: d.id, ...fromFirestore(d.data()), addedAt: d.data().addedAt?.toDate ? d.data().addedAt.toDate().toISOString() : d.data().addedAt }));
  const library = {
    async list(section, { code } = {}) {
      const base = collection(db, 'library');
      const w = [where('section', '==', section), ...(code ? [where('code', '==', code)] : []), orderBy('addedAt', 'desc'), limit(200)];
      const qs = userData?.role === 'admin' ? [query(base, ...w)]
        : [query(base, where('status', '==', 'approved'), where('public', '==', true), ...w), query(base, where('addedBy', '==', user.uid), ...w)];
      const seen = new Set();
      return (await Promise.all(qs.map((q) => getDocs(q)))).flatMap(libRows).filter((x) => !seen.has(x.id) && seen.add(x.id));
    },
    async pending() {
      return libRows(await getDocs(query(collection(db, 'library'), where('status', '==', 'pending'), orderBy('addedAt', 'desc'), limit(200))));
    },
    async add(record, assets) {
      const ref = doc(collection(db, 'library'));
      const files = { spec: `library/${ref.id}/spectrogram.png`, wave: assets.wave ? `library/${ref.id}/waveform.png` : null,
        audio: assets.audio ? `library/${ref.id}/sound.wav` : null };
      await setDoc(ref, { ...toFirestore(record), files, sound: { sampleRate: assets.sampleRate, seconds: assets.seconds, window: assets.window, speed: assets.speed || 1 },
        status: record.share === 'private' ? 'private' : 'pending', addedBy: user.uid, addedAt: serverTimestamp() });
      const up = (await uploadUrls({ entryId: ref.id })).data;           // signed PUT links for exactly these keys
      const put = async (u, blob, type) => { if (!u || !blob) return; const r = await fetch(u, { method: 'PUT', body: blob, headers: { 'Content-Type': type } }); if (!r.ok) throw new Error(`upload ${r.status}`); };
      await Promise.all([put(up.spec, assets.spec, 'image/png'), put(up.wave, assets.wave, 'image/png'), put(up.audio, assets.audio, 'audio/wav')]);
      return ref.id;
    },
    /** admin: add starter / reference entries that are not in the library yet (approved at once; never twice).
     *  list = manifest entries (default: the public starter list of the section); get(path) -> Blob for a picked folder */
    async loadStarter(section, onStep = () => {}, list = null, get = null) {
      const have = new Set((await this.list(section)).filter((x) => x.starterId).map((x) => x.starterId));
      const todo = (list || await loadStarter()).filter((e) => e.section === section && !have.has(e.starterId));
      let n = 0;
      for (const e of todo) {
        const { record: rec0, assets } = await starterAssets(e, get);
        // 10 Oct 2026: the baleen Code-document pictures (no sound) are for ADMINS only, not researchers
        const record = !list && section === 'baleen' ? { ...rec0, public: false, adminOnly: true } : rec0;
        const id = await this.add(record, assets);
        await this.decide(id, 'approved', record.adminOnly ? 'Admin reference call' : 'Starter example');
        n += 1; onStep(n, todo.length);
      }
      return n;
    },
    /** admin: baleen Code-document pictures loaded before 10 Oct 2026 were visible to researchers → delete them and
     *  load them again as admin only (the rules let admins delete, not change "public") */
    async starterToAdminOnly(section, onStep = () => {}) {
      const old = (await this.list(section)).filter((x) => x.starter && x.starterId && !x.adminOnly && x.public !== false);
      for (const x of old) await deleteDoc(doc(db, 'library', x.id));
      onStep(0, old.length);
      return old.length ? this.loadStarter(section, onStep) : 0;
    },
    async decide(id, status, note = '') {
      await updateDoc(doc(db, 'library', id), { status, decidedBy: user.uid, decidedAt: serverTimestamp(), decisionNote: note });
    },
    /** author: a private entry of mine -> also sent to the community library (kept in my library: share 'both') */
    async share(id) {
      await updateDoc(doc(db, 'library', id), { share: 'both', status: 'pending', public: true, sharedAt: serverTimestamp() });
    },
    uid: user.uid,
    fileUrl: async (id, which, key) => (key ? (await libFile({ entryId: id, which })).data.url : null),
    resolve: (u) => u,
  };

  // noise levels (noiseLevels/{fileId}, written by the pipeline; read-only)
  const noise = { list: (scope) => noiseList(db, scope === 'mine' ? { stationIds: stations } : (scope === 'all' && userData?.role === 'admin' ? {} : { publicOnly: true })) };
  return {
    demo: false,
    library,
    noise,
    canMine: stations.length > 0,
    results,
    async list(category, scope, recording = null) {
      const base = collection(db, 'analysisItems');
      if (scope === 'approve') return listApprove(category);
      // 10 Oct 2026: the newest 20 items hid every older recording once a new batch arrived → read the item list of
      // many recordings (small documents), group it by recording, load the bundles of ONE recording (the chosen one,
      // default the newest)
      const open = [where('category', '==', category), where('status', '==', 'open'), orderBy('createdAt', 'desc'), limit(META)];
      // My data: one query per own station (the security rule checks the station of each item)
      const qs = scope === 'mine'
        ? stations.slice(0, 10).map((sid) => query(base, where('stationId', '==', sid), ...open))
        : (scope === 'all' && userData?.role === 'admin' ? [query(base, ...open)] : [query(base, where('public', '==', true), ...open)]);
      // reviewed items too (done / waiting for approval / being reviewed): progress per recording ("12 of 30 done")
      const closedW = [where('category', '==', category), where('status', 'in', ['done', 'to_approve', 'in_progress']), orderBy('createdAt', 'desc'), limit(META)];
      const qc = scope === 'mine'
        ? stations.slice(0, 10).map((sid) => query(base, where('stationId', '==', sid), ...closedW))
        : (scope === 'all' && userData?.role === 'admin' ? [query(base, ...closedW)] : [query(base, where('public', '==', true), ...closedW)]);
      const closedSnaps = await Promise.all(qc.map((q) => getDocs(q).catch(() => null)));
      const doneBy = new Map();
      closedSnaps.filter(Boolean).forEach((sn) => sn.docs.forEach((d) => { const k = recKey(d.data()); doneBy.set(k, (doneBy.get(k) || 0) + 1); }));
      const snaps = await Promise.all(qs.map((q) => getDocs(q)));
      const seen = new Set();
      const items = snaps.flatMap((sn) => sn.docs.map((d) => ({ id: d.id, ...d.data() }))).filter((x) => x.bundleKey && !seen.has(x.id) && seen.add(x.id))
        .sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
      const recs = recordingsOf(items).map((r) => ({ ...r, done: doneBy.get(r.key) || 0 }));
      const pick = (recording && recs.find((r) => r.key === recording)) || recs[0] || null;
      const chosen = pick ? items.filter((it) => recKey(it) === pick.key).slice(0, PER_RECORDING) : [];
      const calls = (await Promise.all(chosen.map((it) => bundleFor(it).catch(() => null)))).filter(Boolean);
      calls.recordings = recs; calls.recording = pick ? pick.key : null;
      return calls;
    },
    async submit(entry, review, { approving = false, finalRole = false } = {}) {
      const item = entry._item; const me = user.uid;
      const ref = doc(db, 'analysisItems', item.id);
      await runTransaction(db, async (tx) => {
        const s = await tx.get(ref); const d = s.data() || {};
        if (d.status === 'in_progress' && d.claimedBy === me) return;
        const stale = d.status === 'in_progress' && d.claimedAt && d.claimedAt.toMillis() < Date.now() - STALE_MS;
        const free = approving ? d.status === 'to_approve' : d.status === 'open';
        if (!(free || stale)) throw new Error('someone else is reviewing this, or it is already done');
        tx.update(ref, { status: 'in_progress', claimedBy: me, claimedAt: serverTimestamp() });
      });
      const b = writeBatch(db);
      b.set(doc(collection(db, 'analysisItems', item.id, 'edits')), { ...toFirestore(review), by: me, createdAt: serverTimestamp() });
      b.set(doc(db, 'analysisItems', item.id, 'label', 'current'), { ...toFirestore({ ...review.label, species: review.species.final, status: review.status }), by: me, at: serverTimestamp() });
      b.set(doc(db, 'analysisItems', item.id, 'verdicts', me), { ...toFirestore({ status: review.status, dismiss: review.dismiss }), by: me, at: serverTimestamp() });
      if (review.species.moveTo) {
        b.update(ref, { category: review.species.moveTo, speciesId: review.species.final.id, speciesSource: 'person', speciesConfirmed: true,
          status: 'open', claimedBy: null, claimedAt: null, movedFrom: review.section, movedBy: me });
      } else if (finalRole) {
        // researcher / admin: final (also when approving a decoder's review)
        b.update(ref, { status: 'done', doneBy: me, doneAt: serverTimestamp(), sessionId: null });
      } else {
        // decoder: waits for a researcher / admin (the review stays in edits/; the latest one is shown to the approver)
        b.update(ref, { status: 'to_approve', reviewedBy: me, reviewedAt: serverTimestamp() });
      }
      await b.commit();
    },
  };
}

// PREVIEW ONLY (REACT_APP_ANALYSIS_DEMO=1): example calls from the engine, reviews kept in the page (and downloadable)
export function demoSource(bundles, examples = null, toResult = null) {
  const saved = [];
  const waiting = [];                                    // decoders' reviews waiting for approval: {category, entry, doc}
  const done = new Set();                               // reviewed: no longer offered in My data / Community
  const approved = new Set();
  const finals = [];                                    // results of final reviews made in this page
  // PREVIEW library: kept in the page; one approved example per tonal section, made from the example calls
  const lib = [];
  let seeded = null;
  let viewer = 'researcher';
  const seed = () => {
    if (!seeded) {
      const starterSeed = loadStarter().then((list) => list.forEach((e, i) => lib.push({ ...e, id: e.starterId, starter: true, status: 'approved',
        addedBy: 'admin', addedAt: new Date(Date.now() - 86400000 * 2 - i * 1000).toISOString(),
        files: { spec: starterUrl(e.files.spec), wave: starterUrl(e.files.wave), audio: starterUrl(e.files.audio) } }))).catch(() => {});
      // preview only: the admin-only reference calls (public/analysis-demo/library-admin-refs, deleted with the rest of
      // analysis-demo before go-live; on the live site the admin loads them once from the folder)
      const refBase = `${process.env.PUBLIC_URL || ''}/analysis-demo/library-admin-refs`;
      const refSeed = fetch(`${refBase}/manifest.json`).then((r) => (r.ok ? r.json() : { entries: [] })).then((d) => (d.entries || []).forEach((e, i) => lib.push({
        ...e, id: e.starterId, starter: true, status: 'approved', addedBy: 'admin', addedAt: new Date(Date.now() - 86400000 * 3 - i * 1000).toISOString(),
        files: { spec: `${refBase}/${e.files.spec}`, wave: e.files.wave && `${refBase}/${e.files.wave}`, audio: e.files.audio && `${refBase}/${e.files.audio}` } }))).catch(() => {});
      seeded = Promise.all([starterSeed, refSeed, ...Object.entries(bundles).filter(([k]) => ['delphinid_tonal', 'manatee'].includes(k)).map(async ([sec, b]) => {
        const c = b.calls[0]; if (!c || !c.audio) return;
        try {
          const a = await makeAssets(c, c.call);
          lib.push({ id: `example-${sec}`, section: sec, code: c.code, broad: c.broad || '', speciesLabel: c.species || null, public: true, stationId: 'example',
            status: 'approved', addedBy: 'example', addedAt: new Date(Date.now() - 86400000).toISOString(), example: true,
            call: c.call, sound: { sampleRate: a.sampleRate, seconds: a.seconds, window: a.window, speed: a.speed || 1 },
            files: { spec: URL.createObjectURL(a.spec), wave: a.wave && URL.createObjectURL(a.wave), audio: a.audio && URL.createObjectURL(a.audio) } });
        } catch (e) { /* preview only */ }
      })]);
    }
    return seeded;
  };
  const library = {
    setViewer(r) { viewer = r; },                                   // preview: what the rules would show this role
    async list(section, { code } = {}) { await seed(); return lib.filter((x) => x.section === section && (!code || x.code === code)
      && (viewer === 'admin' || x.addedBy === 'you' || (x.status === 'approved' && x.public !== false))).sort((p, q) => String(q.addedAt).localeCompare(String(p.addedAt))); },
    async pending() { await seed(); return lib.filter((x) => x.status === 'pending'); },
    async starterToAdminOnly() { return 0; },                                   // preview: nothing stored
    async loadStarter(section, onStep = () => {}, list = null, get = null) {   // the preview starts with the public starters
      await seed();
      if (!list) return 0;
      const have = new Set(lib.map((x) => x.starterId).filter(Boolean)); let n = 0;
      const todo = list.filter((e) => e.section === section && !have.has(e.starterId));
      for (const e of todo) {
        const { record, assets } = await starterAssets(e, get);
        lib.push({ ...record, id: e.starterId, status: 'approved', addedBy: 'admin', addedAt: new Date().toISOString(),
          sound: { sampleRate: assets.sampleRate, seconds: assets.seconds, window: assets.window, speed: assets.speed },
          files: { spec: URL.createObjectURL(assets.spec), wave: assets.wave && URL.createObjectURL(assets.wave), audio: assets.audio && URL.createObjectURL(assets.audio) } });
        n += 1; onStep(n, todo.length);
      }
      return n;
    },
    async add(record, assets) {
      await new Promise((r) => setTimeout(r, 200));
      const id = `lib-${Date.now().toString(36)}`;
      lib.push({ ...record, id, status: record.share === 'private' ? 'private' : 'pending', addedBy: 'you', addedAt: new Date().toISOString(),
        sound: { sampleRate: assets.sampleRate, seconds: assets.seconds, window: assets.window, speed: assets.speed || 1 },
        files: { spec: URL.createObjectURL(assets.spec), wave: assets.wave && URL.createObjectURL(assets.wave), audio: assets.audio && URL.createObjectURL(assets.audio) } });
      return id;
    },
    async decide(id, status, note = '') { const e = lib.find((x) => x.id === id); if (e) Object.assign(e, { status, decidedBy: 'you', decisionNote: note }); },
    async share(id) { const e = lib.find((x) => x.id === id); if (e) Object.assign(e, { share: 'both', status: 'pending', public: true }); },
    uid: 'you',
    fileUrl: async (id, which, key) => key,
    resolve: (u) => u,
  };
  return {
    noise: demoNoise,
    demo: true,
    library,
    canMine: true,
    saved,
    async results(category, scope) {
      const ex = (examples && examples[category]) || [];
      return [...finals.filter((r) => r.category === category && (scope !== 'mine' || r.stationId)), ...ex.filter((r) => scope !== 'mine' || r.stationId)];
    },
    async list(category, scope) {
      if (scope === 'approve') {
        return waiting.filter((w) => w.category === category && !approved.has(w.key))
          .map((w) => ({ ...w.entry, id: `approve-${w.key}`, _prev: w.doc, _prevId: w.key, _approveKey: w.key }));
      }
      const b = bundles[category];
      if (!b) return [];
      return b.calls.filter((c) => !done.has(`${category}:${c.id}`))
        .map((c) => ({ ...c, id: `${scope}-${c.id}`, _key: `${category}:${c.id}`, _meta: { itemId: `demo-${category}-${c.id}`, scope } }));
    },
    async submit(entry, review, { finalRole = false } = {}) {
      await new Promise((r) => setTimeout(r, 250));
      saved.push(review);
      if (typeof window !== 'undefined') window.__REVIEWS__ = saved;
      const final = finalRole && !review.species.moveTo;
      if (final && toResult) finals.unshift(toResult(`demo-${Date.now().toString(36)}`, { category: review.section, public: true, stationId: entry._meta?.scope === 'mine' ? 'demo-station' : null,
        recordedAt: entry.recordedAt || null, doneBy: 'you', reviewedBy: entry._prev?.by || null, doneAt: new Date().toISOString() }, review, 'this page'));
      if (entry._approveKey) { approved.add(entry._approveKey); return; }
      if (!finalRole && !review.species.moveTo) {
        const { _meta, _key, _prev, _prevId, id, ...clean } = entry;
        waiting.push({ key: entry._key, category: review.section, entry: { ...clean, id: entry._key }, doc: review });
      }
      done.add(entry._key);
    },
  };
}
